import React, { useState } from 'react';
import { DatePicker, Form, Input, Select } from 'antd';
import { BankOutlined, MailOutlined, PhoneOutlined, IdcardOutlined, HomeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSignUp } from '../context/SignUpContext';
import StepNavigation from '../components/StepNavigation';
import { validCNPJ } from '../../../../utils/cnpjValidate';
import { BRAZILIAN_STATES } from '../../../../api/signup';

const StepCompanyInfo = () => {
  const [form] = Form.useForm();
  const { formData, updateCompany, updateCompanyAddress, nextStep, prevStep } = useSignUp();
  const [loadingCep, setLoadingCep] = useState(false);

  // Format CNPJ as user types
  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
    if (numbers.length <= 8) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
    if (numbers.length <= 12) return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8)}`;
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5, 8)}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
  };

  // Format phone as user types
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 10) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  // Format CEP as user types
  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  const handleCNPJChange = (e) => {
    const formatted = formatCNPJ(e.target.value);
    form.setFieldValue('tax_id', formatted);
    updateCompany('tax_id', formatted);
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    form.setFieldValue('company_phone', formatted);
    updateCompany('phone', '+55' + formatted.replace(/\D/g, ''));
  };

  const handleCEPChange = async (e) => {
    const formatted = formatCEP(e.target.value);
    form.setFieldValue('company_postal_code', formatted);
    updateCompanyAddress('postal_code', formatted.replace(/\D/g, ''));

    // Auto-fill address from CEP
    const numbers = formatted.replace(/\D/g, '');
    if (numbers.length === 8) {
      setLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${numbers}/json/`);
        const data = await response.json();
        if (!data.erro) {
          form.setFieldsValue({
            company_street: data.logradouro,
            company_neighborhood: data.bairro,
            company_city: data.localidade,
            company_state: data.uf,
          });
          updateCompanyAddress('street', data.logradouro);
          updateCompanyAddress('neighborhood', data.bairro);
          updateCompanyAddress('city', data.localidade);
          updateCompanyAddress('state', data.uf);
        }
      } catch (err) {
        // CEP lookup failed
      } finally {
        setLoadingCep(false);
      }
    }
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      nextStep();
    } catch (err) {
      // Validation failed
    }
  };

  return (
    <div className="step-content">
      <div className="step-header">
        <h2>Dados da empresa</h2>
        <p>Informe os dados da pessoa jurídica.</p>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          name: formData.company.name,
          company_email: formData.company.email,
          company_phone: formData.company.phone ? formatPhone(formData.company.phone.replace('+55', '')) : '',
          tax_id: formData.company.tax_id ? formatCNPJ(formData.company.tax_id) : '',
          opening_date: formData.company.opening_date ? dayjs(formData.company.opening_date) : null,
          company_postal_code: formData.company.address.postal_code ? formatCEP(formData.company.address.postal_code) : '',
          company_street: formData.company.address.street,
          company_number: formData.company.address.number,
          company_complement: formData.company.address.complement,
          company_neighborhood: formData.company.address.neighborhood,
          company_city: formData.company.address.city,
          company_state: formData.company.address.state,
        }}
        requiredMark={false}
      >
        <Form.Item
          name="name"
          label="Razão social"
          rules={[{ required: true, message: 'Digite a razão social' }]}
        >
          <Input
            prefix={<BankOutlined />}
            placeholder="Nome da empresa"
            size="large"
            onChange={(e) => updateCompany('name', e.target.value)}
          />
        </Form.Item>

        <Form.Item
          name="tax_id"
          label="CNPJ"
          rules={[
            { required: true, message: 'Digite o CNPJ' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const numbers = value.replace(/\D/g, '');
                if (!validCNPJ(numbers)) {
                  return Promise.reject('CNPJ inválido');
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            prefix={<IdcardOutlined />}
            placeholder="00.000.000/0000-00"
            size="large"
            onChange={handleCNPJChange}
            maxLength={18}
          />
        </Form.Item>

        <Form.Item
          name="opening_date"
          label="Data de Abertura"
        >
          <DatePicker
            placeholder="DD/MM/AAAA"
            size="large"
            format={{ format: 'DD/MM/YYYY', type: 'mask' }}
            style={{ width: '100%' }}
            disabledDate={(current) => current && current > dayjs().endOf('day')}
            onChange={(date) => updateCompany('opening_date', date ? date.format('YYYY-MM-DD') : '')}
          />
        </Form.Item>

        <div className="form-row">
          <Form.Item
            name="company_email"
            label="E-mail da empresa"
            rules={[
              { required: true, message: 'Digite o e-mail' },
              { type: 'email', message: 'E-mail inválido' },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="empresa@email.com"
              size="large"
              onChange={(e) => updateCompany('email', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            name="company_phone"
            label="Telefone da empresa"
            rules={[
              { required: true, message: 'Digite o telefone' },
              {
                validator: (_, value) => {
                  const numbers = value?.replace(/\D/g, '') || '';
                  if (numbers.length < 10) {
                    return Promise.reject('Telefone inválido');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="(11) 3333-4444"
              size="large"
              onChange={handlePhoneChange}
              maxLength={15}
            />
          </Form.Item>
        </div>

        <div className="form-section-divider">
          <span>Endereço da empresa</span>
        </div>

        <Form.Item
          name="company_postal_code"
          label="CEP"
          rules={[
            { required: true, message: 'Digite o CEP' },
            {
              validator: (_, value) => {
                const numbers = value?.replace(/\D/g, '') || '';
                if (numbers.length !== 8) {
                  return Promise.reject('CEP deve ter 8 dígitos');
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            prefix={<HomeOutlined />}
            placeholder="00000-000"
            size="large"
            onChange={handleCEPChange}
            maxLength={9}
            loading={loadingCep}
          />
        </Form.Item>

        <Form.Item
          name="company_street"
          label="Rua/Avenida"
          rules={[{ required: true, message: 'Digite o endereço' }]}
        >
          <Input
            placeholder="Nome da rua ou avenida"
            size="large"
            onChange={(e) => updateCompanyAddress('street', e.target.value)}
          />
        </Form.Item>

        <div className="form-row">
          <Form.Item
            name="company_number"
            label="Número"
            className="form-item-small"
          >
            <Input
              placeholder="123"
              size="large"
              onChange={(e) => updateCompanyAddress('number', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            name="company_complement"
            label="Complemento"
            className="form-item-large"
          >
            <Input
              placeholder="Sala, andar (opcional)"
              size="large"
              onChange={(e) => updateCompanyAddress('complement', e.target.value)}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="company_neighborhood"
          label="Bairro"
          rules={[{ required: true, message: 'Digite o bairro' }]}
        >
          <Input
            placeholder="Nome do bairro"
            size="large"
            onChange={(e) => updateCompanyAddress('neighborhood', e.target.value)}
          />
        </Form.Item>

        <div className="form-row">
          <Form.Item
            name="company_city"
            label="Cidade"
            className="form-item-large"
            rules={[{ required: true, message: 'Digite a cidade' }]}
          >
            <Input
              placeholder="Nome da cidade"
              size="large"
              onChange={(e) => updateCompanyAddress('city', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            name="company_state"
            label="Estado"
            className="form-item-small"
            rules={[{ required: true, message: 'Selecione' }]}
          >
            <Select
              placeholder="UF"
              size="large"
              showSearch
              optionFilterProp="children"
              onChange={(value) => updateCompanyAddress('state', value)}
            >
              {BRAZILIAN_STATES.map((state) => (
                <Select.Option key={state.code} value={state.code}>
                  {state.code}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Form>

      <StepNavigation
        onNext={handleNext}
        onBack={prevStep}
      />
    </div>
  );
};

export default StepCompanyInfo;
